import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PopupModal from "./PopupModal";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import Popup from 'reactjs-popup';
import axios from "axios";
import Cookies from "js-cookie";
import styles from "./PopupModal.css";

const ShowPopupButton = ({ type, uniqueId }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupTarget, setPopupTarget] = useState(null);
  const [csrfToken, setCsrfToken] = useState(null); // Add this

  useEffect(() => {
    // wait until component is mounted to access DOM
    const el = document.getElementById(`popup-target-${uniqueId}`);
    setPopupTarget(el);
  }, [uniqueId]);

  const handleOpenPopup = () => setShowPopup(true);
  const handleClosePopup = () => setShowPopup(false);

  const buttonProps = {
    black: {
      className: "blackCtaButton",
      label: "Learn more about Mid-Market GCCs"
    },
    blue: {
      className: "blueCtaButton",
      label: "Download the full report to see why Mid-market Global Capability Centers are no longer a subplot — they are writing the next big chapter."
    },
    red: {
      className: "redCtaButton",
      label: "Download The Report"
    }
  };
  const { className, label } = buttonProps[type] || buttonProps["red"];

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("email"); // email | otp | details | success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(300); // seconds countdown
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const otpInputRef = useRef(null);
  const [sendOtpDisabled, setSendOtpDisabled] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [userDetails, setUserDetails] = useState({
    fullName: "",
    companyName: "",
    jobTitle: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState({});
  const getCookieValue = (name) => {
    const cookieStr = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    return cookieStr ? decodeURIComponent(cookieStr.split("=")[1]) : null;
  };

  useEffect(() => {
    if (success.successMsg) {
      const timer = setTimeout(() => {
        setSuccess((prev) => ({ ...prev, successMsg: "" }));
      }, 5000);

      return () => clearTimeout(timer); // Cleanup on unmount or message change
    }
  }, [success.successMsg]);

  useEffect(() => {
    let interval = null;

    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            setIsTimerActive(false);
            setOtpExpired(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timer]);

  useEffect(() => {
    const checkCookie = async () => {
      try {
        const res = await fetch(
          "https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report/api/get-user-cookie.php",
          {
            method: "POST",
            credentials: "include", // Important!
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          }
        );
        const json = await res.json();

        if (json.status === "success") {
          const data = json.data;
          // Set user details
          setEmail(data.email || "");
          setUserDetails({
            fullName: `${data.first_name ?? ""} ${data.last_name ?? ""}`.trim(),
            companyName: data.company_name || "",
            jobTitle: data.designation || "",
          });

          // Show email step, but also show details directly
          setStep("prefilled"); // <-- Custom step when cookie is set
        } else {
          setStep("email"); // Normal flow
        }
      } catch (err) {
        setStep("email");
      }
    };

    checkCookie();
  }, []);

  useEffect(() => {
    let cooldownInterval;
    if (sendOtpDisabled && otpCooldown > 0) {
      cooldownInterval = setInterval(() => {
        setOtpCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownInterval);
            setSendOtpDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(cooldownInterval);
  }, [sendOtpDisabled, otpCooldown]);

  // Add this useEffect to fetch CSRF token
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const apiUrl = 'https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report/api/csrf-token.php';
        const res = await fetch(apiUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const data = await res.json();
        if (data.success) {
          setCsrfToken(data.csrfToken);
        }
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };

    fetchCsrfToken();
  }, []);

  const validateEmail = (email) => {
    const freeemailBlockedRegex =
      /\b(gmail|yahoo|outlook|hotmail|live|rediff|rediffmail|msn|aol|inbox|gmx|me|icloud|aim|orange|rocketmail)\./i;
    const emailBlockedRegex =
      /\b(ey|bcg|bain|mckinsey|gartner|kpmg|deloitte|ansr|everestgrp|forrester|pwc|alumni|kearney|idc|marketsandmarkets|infinitiresearch|evalueserve|avasant|monitordeloitte|isg-one|bah|grailinsights|statxo|wiz-matic|accenture|fticonsulting|iney|avalonglobalresearch|ansrglobal|ernstandyoung|talent500|summit|mercer|nexdigm|apexgcc|aeriestechnology|thbs|parthenoney)\./i;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      setErrors((prev) => ({ ...prev, email: "Business Email is required" }));
      return false;
    }

    if (!regex.test(email)) {
      setErrors((prev) => ({ ...prev, email: "Invalid Email" }));
      return false;
    }

    if (freeemailBlockedRegex.test(email) || emailBlockedRegex.test(email)) {
      setErrors((prev) => ({ ...prev, email: "Please enter Business Email" }));
      return false;
    }
    return true;
  };

  const handleSendOtp = async () => {
    if (sendOtpDisabled || sendingOtp) return;

    setErrors({});
    setSuccess({});
    setSendingOtp(true); // ⏳ Start spinner

    if (!validateEmail(email)) {
      setSendingOtp(false);
      return;
    }

    try {
      const res = await fetch(
        "https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report/api/send-otp.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const text = await res.text();
      const data = JSON.parse(text);

      if (data.success) {
        setSuccess((prev) => ({ ...prev, successMsg: `OTP sent to ${email}` }));
        // setSuccess((prev) => ({ ...prev, successMsg: data.message }));
        setStep("otp");
        setOtp("");
        setTimer(300);
        setOtpExpired(false);
        setIsTimerActive(true);

        setSendOtpDisabled(true);
        setOtpCooldown(300);

        setTimeout(() => {
          if (otpInputRef.current) otpInputRef.current.focus();
        }, 100);
      } else {
        setErrors((prev) => ({ ...prev, general: data.message }));
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        general: "Server error while sending OTP",
      }));
    } finally {
      setSendingOtp(false); // ✅ Stop spinner
    }
  };

  const handleVerifyOtp = async () => {
    setErrors({});
    setSuccess({});

    if (otpExpired) {
      setErrors((prev) => ({
        ...prev,
        otp: "OTP expired. Please request a new one.",
      }));
      return;
    }

    try {
      const res = await fetch(
        "https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report/api/verify-otp.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp }),
        }
      );

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (err) {
        console.error("Invalid JSON from verify-otp.php:", err.message);
        return;
      }

      if (data.success) {
        // Auto-fill form fields if user data is returned
        if (data?.userData) {
          const user = data.userData;
          setEmail(user.email || "");
          setUserDetails({
            fullName: `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
            companyName: user.company_name || "",
            jobTitle: user.designation || "",
          });
          setStep("prefilled");
        } else {
          setStep("details");
        }
      } else {
        setErrors((prev) => ({ ...prev, general: data.message }));
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      alert("Server error while verifying OTP");
    }
  };

  const downloadRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    companyName: "",
    jobTitle: "",
  });

  const handleDownloadChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!userDetails.fullName.trim()) {
      errors.fullName = "Full Name is required";
    }
    if (!userDetails.companyName.trim()) {
      errors.companyName = "Company Name is required";
    }
    if (!userDetails.jobTitle.trim()) {
      errors.jobTitle = "Job Title is required";
    }
    return errors;
  };

  const closePopup = () => {
    setOpen(false);
    // Reset state when closing
    //
  };

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    document.getElementById("mid-market-loader").style.display = "block";
    const validationErrors = validateForm();

    const searchParams = new URLSearchParams(window.location.search);
    const utm = (key) => searchParams.get(key) || "na";
    const hubspotutkCookie = Cookies.get("hubspotutk");

    if (Object.keys(validationErrors).length === 0) {
        const fullName = userDetails.fullName;
        const [firstName, ...lastParts] = userDetails.fullName.split(" ");
        const lastName = lastParts.join(" ");
        const companyName = userDetails.companyName;
        const jobTitle = userDetails.jobTitle;

        const data = {
        submittedAt: Date.now(),
        fields: [
          { objectTypeId: "0-1", name: "firstname", value: firstName },
          ...(lastName ? [{ objectTypeId: "0-1", name: "lastname", value: lastName }] : []),
          { objectTypeId: "0-1", name: "email", value: email },
          { objectTypeId: "0-1", name: "company", value: companyName },
          { objectTypeId: "0-1", name: "jobtitle", value: jobTitle },
          { objectTypeId: "0-1", name: "page_url", value: window.location.href },
          { objectTypeId: "0-1", name: "report_pdf_link", value: "https://media.zinnov.com/wp-content/uploads/2025/04/zinnov-nasscom-mid-market-global-capability-centers-gccs-report-2025.pdf" },
          { objectTypeId: "0-1", name: "report_name", value: "Zinnov Nasscom Mid Market Global Capability Centers Gccs Report 2025" },
          { objectTypeId: "0-1", name: "report_title", value: "Zinnov Nasscom Mid Market Global Capability Centers Gccs Report 2025" },
          { objectTypeId: "0-1", name: "insight_title", value: "Zinnov-nasscom Mid-market Global Capability Centers (gcc) Report Fy2025" },
          { objectTypeId: "0-1", name: "utm_campaign", value: utm("utm_campaign") },
          { objectTypeId: "0-1", name: "utm_content", value: utm("utm_content") },
          { objectTypeId: "0-1", name: "utm_medium", value: utm("utm_medium") },
          { objectTypeId: "0-1", name: "utm_source", value: utm("utm_source") },
          { objectTypeId: "0-1", name: "utm_term", value: utm("utm_term") },
        ],
        context: {
          hutk: hubspotutkCookie,
          pageUri: window.location.href,
          pageName: document.title,
        },
        csrfToken: csrfToken // ← ADD THIS LINE
      };

      try {
          const res = await fetch("https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report/api/save-user.php",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              email,
              firstName,
              lastName,
              companyName,
              jobTitle,
            }),
          }
        );
        const text = await res.text();
        const resData = JSON.parse(text)
        console.log(resData);

        if (resData.success) {
          console.log(resData.success);
          const existingCookie = getCookieValue("ZURD");
          if (!existingCookie) {

            console.log('Cookie not set');
            const cookieRes = await fetch("https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report/api/get-user-cookie.php",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  userRandomid: data.user_id,
                  email: email,
                  fullName: fullName,
                  companyName: companyName,
                  jobTitle: jobTitle,
                }),
              }
            );

            const cookieData = await cookieRes.json();
            if (!cookieData.success) {
              console.warn("Failed to set user cookie:", cookieData.message);
            }
          }

          axios
          .post(
            "https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report/api/download_report.php",
            JSON.stringify(data),
            {
              headers: {
                'Content-Type': 'application/json'
              },
              withCredentials: true
            }
          )
          .then(response => {
            console.log('Form data submitted successfully:', response);
            console.log(response.data.status_code)
            console.log(response.data.message)

            if(response.data.status_code === 400 && response.data.message === "Invalid Email"){
              alert("Invalid Email")
            }else{
              localStorage.setItem("report_pdf_link", "https://media.zinnov.com/wp-content/uploads/2025/04/zinnov-nasscom-mid-market-global-capability-centers-gccs-report-2025.pdf");
            window.location.href =
              "https://zinnov.com/centers-of-excellence/zinnov-nasscom-mid-market-global-capability-centers-gccs-2025-report-thank-you/?report=zinnov-nasscom-mid-market-global-capability-centers-gccs-report-2025.pdf";
            }
          })
          .catch(error => {
            console.error('Error submitting download form:', error);
            if (error.response?.status === 403) {
              alert('Security validation failed. Please refresh the page and try again.');
            } else {
              alert('Failed to submit form. Please try again.');
            }
            document.getElementById("mid-market-loader").removeAttribute("style");
          });

          setFormData({ fullName: "", email: "", companyName: "", jobTitle: "" });
          setErrors({});
          document.getElementById("mid-market-loader").removeAttribute("style");

        } else {
          alert("Failed to save user details");
          document.getElementById("mid-market-loader").removeAttribute("style");
        }

      } catch (error) {
        alert("Server error while saving user details");
        document.getElementById("mid-market-loader").removeAttribute("style");
      }

    } else {
      setErrors(validationErrors);
      document.getElementById("mid-market-loader").removeAttribute("style");
    }
  };

  return (
    <div>
      <button className={className} onClick={() => setShowPopup(true)}>
        {type === "blue" ? (
          <span>
            <span style={{ color: "#35b3ea" }}>Download the report</span>{" "}
            to see why Mid-market Global Capability Centers are no longer a subplot — they are writing the next big chapter.
          </span>
        ) : (
          label
        )}
      </button>

    {showPopup && popupTarget &&
        ReactDOM.createPortal(
      <PopupModal
        id="PopUp"
        className="common-popup-container"
        show={showPopup} onClose={handleClosePopup}
        modal
        nested
      >
        <img
          id="common-popup-close-button"
          class="common-popup-close"
          src="https://zinnov.com/wp-content/themes/zinnov/assets/icons/close.svg"
          alt="close button"
          onClick={handleClosePopup}
        />
        <form
          ref={downloadRef}
          name="downloadform"
          id="downloadform"
          className="contactform"
          onSubmit={handleDownloadSubmit}
        >
          <div className="p-6 bg-white rounded shadow-md w-[360px] max-w-full">
            {(step === "email" || step === "prefilled") && (
              <div>
              {errors.general && <span className="input-error">{errors.general}</span>}
              {success.successMsg && <span className="input-success">{success.successMsg}</span>}
                <div className="common-popup-input-error-container">
                  <input
                    type="email"
                    placeholder="Business Email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="report-common-popup-input"
                    readOnly={step === "prefilled"} // optional
                  />
                  {errors.email && (
                    <span className="input-error">{errors.email}</span>
                  )}
                </div>

                {step === "email" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSendOtp();
                    }}
                    className="report-common-popup-submit"
                    disabled={sendOtpDisabled || sendingOtp}
                  >
                    {sendingOtp ? (
                      <>
                        <span className="spinner" /> Sending...
                      </>
                    ) : sendOtpDisabled ? (
                      `Please wait (${otpCooldown}s)`
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                )}

                {step === "prefilled" && (
                  <div>
                    {errors.general && <span className="input-error">{errors.general}</span>}
                    {success.successMsg && <span className="input-success">{success.successMsg}</span>}
                    {/* Prefilled details form directly under email */}
                    <div className="common-popup-input-error-container">
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Full Name"
                        value={userDetails.fullName}
                        onChange={handleDownloadChange}
                        className="common-popup-input"
                      />
                      {errors.fullName && (
                        <span className="input-error">{errors.fullName}</span>
                      )}
                    </div>
                    <div className="common-popup-input-error-container">
                      <input
                        type="text"
                        name="companyName"
                        placeholder="Company Name"
                        value={userDetails.companyName}
                        onChange={handleDownloadChange}
                        className="common-popup-input"
                      />
                      {errors.companyName && (
                        <span className="input-error">
                          {errors.companyName}
                        </span>
                      )}
                    </div>
                    <div className="common-popup-input-error-container">
                      <input
                        type="text"
                        name="jobTitle"
                        placeholder="Job Title"
                        value={userDetails.jobTitle}
                        onChange={handleDownloadChange}
                        className="common-popup-input"
                      />
                      {errors.jobTitle && (
                        <span className="input-error">{errors.jobTitle}</span>
                      )}
                    </div>

                    <input
                      type="submit"
                      value="DOWNLOAD"
                      className="common-popup-submit"
                    />
                  </div>
                )}
              </div>
            )}

            {step === "otp" && (
              <div>
                {errors.general && (
                  <span className="input-error">{errors.general}</span>
                )}
                {success.successMsg && (
                  <span className="input-success">{success.successMsg}</span>
                )}

                <div className="common-popup-input-error-container">
                  <input
                    type="text"
                    name="otp"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    ref={otpInputRef}
                    className="report-common-popup-input"
                    disabled={otpExpired} // ✅ disable input if expired
                  />
                  {errors.otp && (
                    <span className="input-error">{errors.otp}</span>
                  )}
                </div>

                <div className="otpInlineButton">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleVerifyOtp();
                    }}
                    className={`report-common-popup-submit ${
                      otpExpired ? "report-common-popup-disabled" : ""
                    }`}
                    disabled={otpExpired} // ✅ disable verify button if expired
                  >
                    {otpExpired ? "OTP Expired" : `Verify OTP (${timer}s)`}
                  </button>

                  {otpExpired && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="report-common-popup-submit mt-2"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            )}
            {step === "details" && (
              <div>
                {errors.general && (
                  <span className="input-error">{errors.general}</span>
                )}
                {/* <h2 className="text-xl font-bold mb-4">Your Details</h2> */}
                <div class="common-popup-input-error-container">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={userDetails.fullName}
                    onChange={handleDownloadChange}
                    className="common-popup-input"
                  />
                  {errors.fullName && (
                    <span className="input-error">{errors.fullName}</span>
                  )}
                </div>
                <div class="common-popup-input-error-container">
                  <input
                    type="text"
                    name="companyName"
                    placeholder="Company Name"
                    value={userDetails.companyName}
                    onChange={handleDownloadChange}
                    className="common-popup-input"
                  />
                  {errors.companyName && (
                    <span className="input-error">{errors.companyName}</span>
                  )}
                </div>
                <div class="common-popup-input-error-container">
                  <input
                    type="text"
                    name="jobTitle"
                    placeholder="Job Title"
                    value={userDetails.jobTitle}
                    onChange={handleDownloadChange}
                    className="common-popup-input"
                  />
                  {errors.jobTitle && (
                    <span className="input-error">{errors.jobTitle}</span>
                  )}
                </div>

                <input
                  type="submit"
                  value="DOWNLOAD"
                  className="common-popup-submit"
                />
              </div>
            )}
          </div>
        </form>
        <div id="mid-market-loader" className={styles["sticky-spinner-loader"]}>
            <div className={styles["spinner-loader"]}><div></div><div></div><div></div><div></div></div>
          </div>
      </PopupModal>,
          popupTarget
        )
      }
    </div>
  );
};

export default ShowPopupButton;